import type { PlanCell, PlanAisle, PlanShelf, CellUpdate, WorkshopPlan } from '@features/workshop-plan/types';

/** What the order of a cell is waiting for, or doing, at the workshop: it sets the color of the cell. */
export type CellTone = 'empty' | 'waiting' | 'inProgress' | 'finished' | 'other';

const STATE_TONES: Record<string, { tone: CellTone; label: string }> = {
  workshop_stored: { tone: 'waiting', label: 'En attente de personnalisation' },
  workshop_in_progress: { tone: 'inProgress', label: 'Personnalisation en cours' },
  workshop_finished: { tone: 'finished', label: "Personnalisation terminée : à sortir de l'atelier" },
};

export interface CellStatus {
  occupied: boolean;
  tone: CellTone;
  /** Meaning of the tone, null when the cell is empty or its order has a status the workshop does not track. */
  stateLabel: string | null;
  /** Short text shown under the cell name: the order reference, or "Vide". */
  summary: string;
  /** Full sentence for assistive technologies. */
  description: string;
  /** Lines of the tooltip: everything the tile does not have room to show. */
  details: string[];
}

/** A cell is occupied when its box carries an order; its tone follows the status of that order. */
export const getCellStatus = (cell: PlanCell): CellStatus => {
  const place = cell.level === null ? [] : [`Niveau ${cell.level}`];

  if (cell.order === null) {
    return {
      occupied: false,
      tone: 'empty',
      stateLabel: null,
      summary: 'Vide',
      description: `${cell.name} : vide`,
      details: [cell.name, ...place, ...(cell.box ? [`Caisse ${cell.box}`] : []), 'Vide'],
    };
  }

  const known = cell.state === null ? undefined : STATE_TONES[cell.state];

  return {
    occupied: true,
    tone: known?.tone ?? 'other',
    stateLabel: known?.label ?? null,
    summary: `#${cell.order}`,
    description: `${cell.name} : commande ${cell.order}${known ? `, ${known.label.toLowerCase()}` : ''}`,
    details: [
      cell.name,
      ...place,
      ...(cell.box ? [`Caisse ${cell.box}`] : []),
      `Commande ${cell.order}`,
      ...(known ? [known.label] : []),
      ...(known?.tone === 'finished' && cell.box ? ["À sortir depuis l'écran Sortie caisses"] : []),
    ],
  };
};

/** What follows the last dash of a location name ("GRAV-12" → "12"): the prefix is the same all over a zone. */
export const getShortName = (name: string): string => {
  const short = name.slice(name.lastIndexOf('-') + 1).trim();
  return short === '' ? name : short;
};

export interface ToneCounts {
  waiting: number;
  inProgress: number;
  finished: number;
}

/** Cells by what their order is doing: the legend of the plan. */
export const countTones = (cells: PlanCell[]): ToneCounts => {
  const counts: ToneCounts = { waiting: 0, inProgress: 0, finished: 0 };

  cells.forEach((cell) => {
    const { tone } = getCellStatus(cell);
    if (tone === 'waiting' || tone === 'inProgress' || tone === 'finished') counts[tone] += 1;
  });

  return counts;
};

/** What the operator is looking for on the plan: the cells of a tone and/or the ones matching a typed reference. */
export interface PlanFocus {
  tone: CellTone | null;
  query: string;
}

export const NO_FOCUS: PlanFocus = { tone: null, query: '' };

/** True when the cell is part of what is looked for; with no focus every cell matches. */
export const matchesFocus = (cell: PlanCell, focus: PlanFocus): boolean => {
  if (focus.tone !== null && getCellStatus(cell).tone !== focus.tone) return false;

  const query = focus.query.trim().toLowerCase();
  if (query === '') return true;

  return [cell.order, cell.box, cell.name].some((value) => value?.toLowerCase().includes(query));
};

export const getAisleTitle = (aisle: PlanAisle): string => `${aisle.code} — ${aisle.name}`;

export const getPlanTitle = (plan: WorkshopPlan): string => (plan.code ? `${plan.code} — ${plan.name}` : plan.name);

export const getPlanCells = (plans: WorkshopPlan[]): PlanCell[] =>
  plans.flatMap((plan) => [
    ...plan.aisles.flatMap((aisle) => aisle.shelves.flatMap((shelf) => shelf.cells)),
    ...plan.floorCells,
  ]);

/** Shelves that hold at least one placed cell: an empty shelf has nothing to show. */
export const getFilledShelves = (aisle: PlanAisle): PlanShelf[] =>
  aisle.shelves.filter((shelf) => shelf.cells.length > 0);

/** Cells starting on one level of a shelf: a band spans one or more levels and splits its width equally. */
export interface ShelfBand {
  level: number;
  span: number;
  cells: PlanCell[];
}

/** Bands of a shelf by starting level (top first), their cells ordered by rank. */
export const getBands = (shelf: PlanShelf): ShelfBand[] => {
  const bands = new Map<number, ShelfBand>();

  shelf.cells.forEach((cell) => {
    if (cell.level === null) return;
    const band = bands.get(cell.level) ?? { level: cell.level, span: cell.levelSpan, cells: [] };
    band.cells.push(cell);
    bands.set(cell.level, band);
  });

  return [...bands.values()]
    .map((band) => ({ ...band, cells: [...band.cells].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)) }))
    .sort((a, b) => a.level - b.level);
};

/** Relative width of a shelf: the cell count of its fullest band (one at least). */
export const getShelfWeight = (shelf: PlanShelf): number =>
  Math.max(1, ...getBands(shelf).map((band) => band.cells.length));

/** Relative width of an aisle: its non-empty shelves are side by side. */
export const getAisleWeight = (aisle: PlanAisle): number =>
  getFilledShelves(aisle).reduce((sum, shelf) => sum + getShelfWeight(shelf), 0);

/** Relative width of the whole zone: aisles are side by side too. */
export const getZoneWeight = (plan: WorkshopPlan): number =>
  plan.aisles.reduce((sum, aisle) => sum + getAisleWeight(aisle), 0);

/** Plans where the cell of the update shows its new box, order and order status; the other cells are left untouched. */
export const applyCellUpdate = (plans: WorkshopPlan[], update: CellUpdate): WorkshopPlan[] => {
  const patch = (cell: PlanCell): PlanCell =>
    cell.id === update.id ? { ...cell, box: update.box, order: update.order, state: update.state } : cell;

  return plans.map((plan) => ({
    ...plan,
    aisles: plan.aisles.map((aisle) => ({
      ...aisle,
      shelves: aisle.shelves.map((shelf) => ({ ...shelf, cells: shelf.cells.map(patch) })),
    })),
    floorCells: plan.floorCells.map(patch),
  }));
};
